import React, { useEffect, useState } from "react";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
} from "recharts";
import api from "../lib/api";

export default function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const { data: full } = await api.get("/analytics/full");
        setData(full);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) return <div className="text-center text-[#A3A3A3] py-20 text-sm font-mono">Loading analytics...</div>;

  const trends = data?.trends || [];
  const vsBowlers = data?.vs_bowler_types || [];
  const s = data?.summary || {};

  const radarData = [
    { subject: "Batting Avg", A: Math.min(100, (s.batting_average || 0) * 2), fullMark: 100 },
    { subject: "Strike Rate", A: Math.min(100, (s.strike_rate || 0) / 2), fullMark: 100 },
    { subject: "Boundary %", A: s.boundary_percentage || 0, fullMark: 100 },
    { subject: "Consistency", A: s.consistency_score || 0, fullMark: 100 },
    { subject: "Bowling Impact", A: Math.min(100, (s.total_wickets || 0) * 8), fullMark: 100 },
    { subject: "Economy Ctrl", A: Math.max(0, 100 - (s.bowling_economy || 12) * 8), fullMark: 100 },
  ];

  return (
    <div className="space-y-8 animate-fadeIn">
      <div className="bg-[#141414] border border-[#262626] p-6 rounded-xl flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-black uppercase text-white">Advanced Cricket Analytics</h3>
          <p className="text-xs text-[#A3A3A3]">Pandas-powered engine • Strike-rate progression • Dot-ball ratios • Bowler-type analysis</p>
        </div>
        <span className="bg-emerald-500/20 text-emerald-400 text-xs font-mono px-3 py-1 rounded border border-emerald-500/30">
          Engine Active
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <ChartCard title="Match Runs Trend" subtitle="Per-innings scoring velocity">
          {trends.length === 0 ? <Empty /> : (
            <BarChart data={trends}>
              <XAxis dataKey="match" stroke="#737373" fontSize={12} tickLine={false} />
              <YAxis stroke="#737373" fontSize={12} tickLine={false} />
              <Tooltip contentStyle={ttStyle} />
              <Bar dataKey="runs" fill="#FF3B30" radius={[4, 4, 0, 0]} />
            </BarChart>
          )}
        </ChartCard>

        <ChartCard title="Strike Rate Trend" subtitle="Scoring efficiency per fixture">
          {trends.length === 0 ? <Empty /> : (
            <LineChart data={trends}>
              <XAxis dataKey="match" stroke="#737373" fontSize={12} tickLine={false} />
              <YAxis stroke="#737373" fontSize={12} tickLine={false} />
              <Tooltip contentStyle={ttStyle} />
              <Line type="monotone" dataKey="strike_rate" stroke="#34C759" strokeWidth={3} dot={{ fill: "#34C759", r: 4 }} />
            </LineChart>
          )}
        </ChartCard>

        <ChartCard title="Wickets Per Match" subtitle="Bowling impact tracker">
          {trends.length === 0 ? <Empty /> : (
            <BarChart data={trends}>
              <XAxis dataKey="match" stroke="#737373" fontSize={12} tickLine={false} />
              <YAxis stroke="#737373" fontSize={12} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={ttStyle} />
              <Bar dataKey="wickets" fill="#007AFF" radius={[4, 4, 0, 0]} />
            </BarChart>
          )}
        </ChartCard>

        <ChartCard title="Performance vs Bowler Type" subtitle="Runs & strike rate against each bowling style">
          {vsBowlers.length === 0 ? <Empty /> : (
            <BarChart data={vsBowlers}>
              <XAxis dataKey="bowler_type" stroke="#737373" fontSize={11} tickLine={false} />
              <YAxis stroke="#737373" fontSize={12} tickLine={false} />
              <Tooltip contentStyle={ttStyle} />
              <Bar dataKey="runs" fill="#FF9500" radius={[4, 4, 0, 0]} />
              <Bar dataKey="strike_rate" fill="#AF52DE" radius={[4, 4, 0, 0]} />
            </BarChart>
          )}
        </ChartCard>
      </div>

      <div className="bg-[#141414] border border-[#262626] rounded-xl p-6 space-y-6">
        <div>
          <h4 className="text-lg font-bold uppercase text-white">Player Skill Radar Profile</h4>
          <p className="text-xs text-[#A3A3A3]">Multi-dimensional evaluation of batting, bowling & consistency</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
          <div className="md:col-span-1 space-y-4">
            <SkillTile label="Batting Average" value={s.batting_average || 0} note="Overall runs per dismissal." />
            <SkillTile label="Strike Rate" value={s.strike_rate || 0} note="Scoring velocity across formats." />
            <SkillTile label="Boundary %" value={`${s.boundary_percentage || 0}%`} note="Share of runs from 4s and 6s." />
            <SkillTile label="Consistency" value={s.consistency_score || 0} note="Lower variance = higher score." />
          </div>
          <div className="md:col-span-2 h-96 flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart cx="50%" cy="50%" outerRadius={120} data={radarData}>
                <PolarGrid stroke="#262626" />
                <PolarAngleAxis dataKey="subject" stroke="#A3A3A3" fontSize={11} />
                <PolarRadiusAxis angle={30} domain={[0, 100]} stroke="#737373" />
                <Radar name="Player" dataKey="A" stroke="#FF3B30" fill="#FF3B30" fillOpacity={0.4} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}

const ttStyle = { backgroundColor: "#141414", borderColor: "#262626", borderRadius: 8, color: "#fff" };

function ChartCard({ title, subtitle, children }) {
  return (
    <div className="bg-[#141414] border border-[#262626] rounded-xl p-6 space-y-4">
      <div>
        <h4 className="text-lg font-bold uppercase text-white">{title}</h4>
        <p className="text-xs text-[#A3A3A3]">{subtitle}</p>
      </div>
      <div className="h-72 w-full">
        <ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer>
      </div>
    </div>
  );
}

function SkillTile({ label, value, note }) {
  return (
    <div className="bg-[#1A1A1A] p-4 rounded-lg border border-[#262626]">
      <span className="text-xs text-[#A3A3A3] font-mono block">{label}</span>
      <span className="text-2xl font-black text-white">{value}</span>
      <p className="text-xs text-emerald-400 mt-1">{note}</p>
    </div>
  );
}

function Empty() {
  return <div className="w-full h-full flex items-center justify-center text-xs text-[#737373] font-mono uppercase">Log matches to see analytics</div>;
}
